import { Activity, CircleCheck, FlaskConical, ShieldCheck, TestTube, Timer } from 'lucide-react';

/*
 * The sign-in page's brand panel: a honeycomb lattice drifting behind a DNA
 * helix, a bubbling flask, ball-and-stick molecules and a heartbeat trace.
 *
 * Everything is inline SVG animated from login.css — the production CSP allows
 * no third-party images, and a medical app should not depend on someone else's
 * CDN to render its front door. The geometry is computed once at module load.
 */

// Pointy-top hexagon of circumradius R; one tile is a full hexagon plus the
// vertical edge joining it to the row below, which tiles seamlessly.
const R = 26;
const HEX_W = Math.sqrt(3) * R;
const HEX_H = 3 * R;
const HEX_TILE = [
  `M${HEX_W / 2} 0 L${HEX_W} ${R / 2} L${HEX_W} ${1.5 * R} L${HEX_W / 2} ${2 * R}`,
  `L0 ${1.5 * R} L0 ${R / 2} Z`,
  `M${HEX_W / 2} ${2 * R} L${HEX_W / 2} ${HEX_H}`,
].join(' ');

const HELIX = { x: 455, top: 30, height: 460, amplitude: 52, turns: 2.5, rungs: 22 };

function helixStrand(phase: number): string {
  const { x, top, height, amplitude, turns } = HELIX;
  const points: string[] = [];
  for (let step = 0; step <= 120; step++) {
    const t = step / 120;
    const px = x + amplitude * Math.sin(t * turns * 2 * Math.PI + phase);
    points.push(`${px.toFixed(1)} ${(top + t * height).toFixed(1)}`);
  }
  return `M${points.join(' L')}`;
}

const STRAND_A = helixStrand(0);
const STRAND_B = helixStrand(Math.PI);

const RUNGS = Array.from({ length: HELIX.rungs }, (_, index) => {
  const { x, top, height, amplitude, turns } = HELIX;
  const t = (index + 0.5) / HELIX.rungs;
  const angle = t * turns * 2 * Math.PI;
  const offset = amplitude * Math.sin(angle);
  // cos > 0 is the half of the turn nearest the viewer: larger, brighter ends.
  const depth = Math.cos(angle);
  return { y: top + t * height, x1: x + offset, x2: x - offset, depth };
});

// A resting baseline broken by a P wave, a QRS spike and a T wave, repeated.
const ECG_BEAT = 'l40 0 q8 -12 16 0 l14 0 l6 10 l10 -64 l10 76 l8 -22 l18 0 q12 -22 24 0 l54 0';
const ECG = `M-20 60 ${ECG_BEAT.repeat(4)}`;

interface Molecule {
  x: number;
  y: number;
  scale: number;
}

// Each molecule is one central atom with three bonded neighbours.
const MOLECULES: Molecule[] = [
  { x: 84, y: 104, scale: 1 },
  { x: 320, y: 200, scale: 0.7 },
  { x: 320, y: 400, scale: 0.85 },
  { x: 560, y: 470, scale: 0.6 },
  { x: 52, y: 470, scale: 0.75 },
];
const BONDS = [
  [0, -34],
  [30, 18],
  [-30, 18],
] as const;

const BUBBLES = [
  { cx: 150, r: 5 },
  { cx: 170, r: 3.5 },
  { cx: 186, r: 6 },
  { cx: 162, r: 2.5 },
  { cx: 178, r: 4 },
];

const CARDS = [
  { icon: <TestTube />, title: 'Sample received', meta: 'CBC · Lab #4821' },
  { icon: <CircleCheck />, title: 'Report verified', meta: 'Signed by pathologist' },
  { icon: <Timer />, title: 'Turnaround', meta: 'Results within 2 hours' },
];

const HIGHLIGHTS = [
  { icon: <FlaskConical />, label: 'Sample tracking' },
  { icon: <ShieldCheck />, label: 'Verified reports' },
  { icon: <Activity />, label: 'Live analytics' },
];

function HexLattice() {
  return (
    // Oversized by one tile up and left, so drifting by one tile loops seamlessly.
    <svg
      className="c-lab-art__lattice"
      style={{ '--hex-w': `${HEX_W}px`, '--hex-h': `${HEX_H}px` } as React.CSSProperties}
    >
      <defs>
        <pattern id="lab-hex" width={HEX_W} height={HEX_H} patternUnits="userSpaceOnUse">
          <path d={HEX_TILE} />
          <circle cx={HEX_W / 2} cy={2 * R} r="2.5" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#lab-hex)" />
    </svg>
  );
}

function Helix() {
  return (
    <g className="c-lab-art__helix">
      {RUNGS.map((rung, index) => (
        <line
          key={rung.y}
          className="c-lab-art__rung"
          x1={rung.x1}
          x2={rung.x2}
          y1={rung.y}
          y2={rung.y}
          style={{ animationDelay: `${index * 120}ms` }}
        />
      ))}
      <path className="c-lab-art__strand" d={STRAND_A} />
      <path className="c-lab-art__strand c-lab-art__strand--back" d={STRAND_B} />
      {RUNGS.map((rung) => (
        <g key={rung.y}>
          <circle cx={rung.x1} cy={rung.y} r={3 + rung.depth * 1.5} />
          <circle cx={rung.x2} cy={rung.y} r={3 - rung.depth * 1.5} />
        </g>
      ))}
    </g>
  );
}

function Flask() {
  return (
    <g className="c-lab-art__flask">
      <path d="M146 250 h48 M154 250 v70 l-56 104 a16 16 0 0 0 14 24 h120 a16 16 0 0 0 14 -24 l-56 -104 v-70" />
      <path
        className="c-lab-art__liquid"
        d="M121 380 q25 -10 49 0 t49 0 l25 46 a16 16 0 0 1 -14 22 h-120 a16 16 0 0 1 -14 -22 z"
      />
      {BUBBLES.map((bubble, index) => (
        <circle
          key={bubble.cx}
          className="c-lab-art__bubble"
          cx={bubble.cx}
          cy={420}
          r={bubble.r}
          style={{ animationDelay: `${index * 700}ms` }}
        />
      ))}
    </g>
  );
}

function Molecules() {
  return MOLECULES.map((molecule, index) => (
    <g
      key={`${molecule.x},${molecule.y}`}
      transform={`translate(${molecule.x} ${molecule.y}) scale(${molecule.scale})`}
    >
      <g className="c-lab-art__molecule" style={{ animationDelay: `${index * -1.6}s` }}>
        {BONDS.map(([dx, dy]) => (
          <line key={`${dx},${dy}`} x1={0} y1={0} x2={dx} y2={dy} />
        ))}
        <circle r="9" />
        {BONDS.map(([dx, dy]) => (
          <circle key={`${dx},${dy}`} cx={dx} cy={dy} r="5.5" />
        ))}
      </g>
    </g>
  ));
}

export function LabArtwork() {
  return (
    <div className="c-lab-art" aria-hidden="true">
      <HexLattice />

      <div className="c-lab-art__stage">
        <svg className="c-lab-art__scene" viewBox="0 0 600 520">
          <Helix />
          <Flask />
          <Molecules />
        </svg>

        <ul className="c-lab-art__cards">
          {CARDS.map((card) => (
            <li key={card.title} className="c-lab-art__card">
              <span className="c-lab-art__card-icon">{card.icon}</span>
              <span>
                <span className="c-lab-art__card-title">{card.title}</span>
                <span className="c-lab-art__card-meta">{card.meta}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <svg className="c-lab-art__pulse" viewBox="0 0 600 90" preserveAspectRatio="none">
        <path d={ECG} pathLength={1} />
      </svg>

      <div className="c-lab-art__copy">
        <p className="c-lab-art__eyebrow">NDC Diagnostic Centre</p>
        <p className="c-lab-art__headline">Precision in every sample.</p>
        <p className="c-lab-art__tagline">
          From collection to a verified report — one connected workflow for your whole lab.
        </p>
        <ul className="c-lab-art__highlights">
          {HIGHLIGHTS.map((highlight) => (
            <li key={highlight.label}>
              {highlight.icon}
              {highlight.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
